import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Tag, Plus, Trash2, Search } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { showErrorToast } from '../../utils/errorHandling';

const TagMappingModal = ({ isOpen, kit, onClose, onTagsUpdated }) => {
  const [formData, setFormData] = useState({
    tags: []
  });
  const [loading, setLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && kit) {
      setFormData({
        tags: kit.tags || []
      });
      loadAvailableTags();
    }
  }, [isOpen, kit]);

  const loadAvailableTags = async () => {
    try {
      const response = await superAdminAPI.getAllTags();
      setAvailableTags(response.data || []);
    } catch (error) {
      console.error('Error loading tags:', error);
      // Fallback to common tags
      setAvailableTags([
        'mathematics', 'physics', 'chemistry', 'biology',
        'algebra', 'calculus', 'geometry', 'trigonometry',
        'mechanics', 'thermodynamics', 'optics', 'electricity',
        'organic-chemistry', 'inorganic-chemistry', 'physical-chemistry',
        'fundamentals', 'intermediate', 'advanced',
        'jee-main', 'jee-advanced', 'neet', 'bitsat',
        '2024', '2023', '2022', 'real-exam', 'practice',
        'problem-solving', 'conceptual', 'numerical'
      ]);
    }
  };

  const handleAddTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setNewTag('');
      setSearchTerm('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddNewTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      handleAddTag(tag);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.tags.length === 0) {
      newErrors.tags = 'At least one tag is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await superAdminAPI.updateKitTags(kit.id, { tags: formData.tags });
      onTagsUpdated();
    } catch (error) {
      console.error('Error updating kit tags:', error);
      showErrorToast(error, 'Failed to update kit tags');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ tags: [] });
      setNewTag('');
      setSearchTerm('');
      setErrors({});
      onClose();
    }
  };

  const getFilteredTags = () => {
    return availableTags.filter(tag => 
      tag.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !formData.tags.includes(tag)
    );
  };

  const getTagsByCategory = () => {
    const categories = {
      'Subjects': ['mathematics', 'physics', 'chemistry', 'biology'],
      'Topics': ['algebra', 'calculus', 'geometry', 'trigonometry', 'mechanics', 'thermodynamics', 'optics', 'electricity'],
      'Chemistry': ['organic-chemistry', 'inorganic-chemistry', 'physical-chemistry'],
      'Difficulty': ['fundamentals', 'intermediate', 'advanced'],
      'Exams': ['jee-main', 'jee-advanced', 'neet', 'bitsat'],
      'Years': ['2024', '2023', '2022'],
      'Types': ['real-exam', 'practice', 'problem-solving', 'conceptual', 'numerical']
    };

    const filteredTags = getFilteredTags();
    const categorized = {};
    const uncategorized = [];

    filteredTags.forEach(tag => {
      let found = false;
      for (const [category, tags] of Object.entries(categories)) {
        if (tags.includes(tag)) {
          if (!categorized[category]) categorized[category] = [];
          categorized[category].push(tag);
          found = true;
          break;
        }
      }
      if (!found) {
        uncategorized.push(tag);
      }
    });

    if (uncategorized.length > 0) {
      categorized['Other'] = uncategorized;
    }

    return categorized;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Tag className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Manage Kit Tags</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Kit Info */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">{kit?.name}</h3>
          <p className="text-sm text-gray-600 mt-1">{kit?.description}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Current Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Current Tags ({formData.tags.length})
            </label>
            <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border border-gray-300 rounded-lg bg-gray-50">
              {formData.tags.length > 0 ? (
                formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                      disabled={loading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))
              ) : (
                <span className="text-gray-500 text-sm">No tags selected</span>
              )}
            </div>
            {errors.tags && <p className="mt-1 text-sm text-red-600">{errors.tags}</p>}
          </div>

          {/* Add New Tag */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add New Tag
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Enter new tag..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddNewTag())}
                disabled={loading}
              />
              <button
                type="button"
                onClick={handleAddNewTag}
                disabled={!newTag.trim() || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Search Available Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add from Available Tags
            </label>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search available tags..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>

            {/* Available Tags by Category */}
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {Object.entries(getTagsByCategory()).map(([category, tags]) => (
                <div key={category} className="p-3 border-b border-gray-100 last:border-b-0">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">{category}</h4>
                  <div className="flex flex-wrap gap-1">
                    {tags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleAddTag(tag)}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-800 transition-colors"
                        disabled={loading}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              
              {Object.keys(getTagsByCategory()).length === 0 && (
                <div className="p-4 text-center text-gray-500">
                  <Tag className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No available tags found</p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Update Tags
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TagMappingModal;
